import { usePostHog } from '@posthog/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Domain } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import type { IEntriCloseDetail } from '@feathr/extender/integrations/entri';
import { loadEntriSdk, onEntriClosed } from '@feathr/extender/integrations/entri';
import {
  EEntriOutcome,
  EEntriTokenError,
  getEntriOutcome,
  getEntriTokenError,
  getStorableProvider,
  toEntriConfig,
} from '@feathr/extender/integrations/entri.utils';
import { useFlags, useRole } from '@feathr/extender/state';

export interface IUseEntriConnect {
  canConnect: (domain: Domain) => boolean;
  isOpening: boolean;
  openEntri: (domain: Domain) => Promise<void>;
}

/** Opens Entri's guided DNS setup for a domain and turns its outcome into toasts, state, and analytics. */
function useEntriConnect(): IUseEntriConnect {
  const { t } = useTranslation();
  const flags = useFlags();
  const { hasDomains } = useRole();
  const posthog = usePostHog();
  const [isOpening, setIsOpening] = useState(false);

  function canConnect(domain: Domain): boolean {
    return !!flags.entriDomainAuth && hasDomains && domain.needsEmailAuthentication(flags.bypassMX);
  }

  async function handleSuccess(domain: Domain, detail: IEntriCloseDetail): Promise<void> {
    toast(t('Domain records were added. Changes may take up to 48 hours to go live.'), {
      type: 'success',
    });
    const provider = getStorableProvider(detail);
    if (provider) {
      await domain.patch({ dns_provider: provider });
    }
    await domain.reload();
  }

  async function handleClose(domain: Domain, detail: IEntriCloseDetail): Promise<void> {
    posthog?.capture('entri_closed', {
      domainId: domain.id,
      lastStatus: detail.lastStatus,
      provider: detail.provider,
      setupType: detail.setupType,
      success: detail.success,
    });

    switch (getEntriOutcome(detail)) {
      case EEntriOutcome.Success:
        await handleSuccess(domain, detail);
        break;

      case EEntriOutcome.LinkShared:
        toast(t('Shared setup. Changes may take up to 48 hours to go live.'), { type: 'success' });
        break;

      case EEntriOutcome.Error:
        toast(t("Couldn't finish domain setup. Add the records manually or contact support."), {
          type: 'error',
        });
        break;

      case EEntriOutcome.Dismissed:
        break;
    }
  }

  function handleOpenError(error: unknown): void {
    const message =
      getEntriTokenError(error) === EEntriTokenError.Unsupported
        ? t(
            "Automatic setup isn't available for this domain. Add the records to your DNS host manually.",
          )
        : t("Couldn't open domain setup. Try again or add the records manually.");
    toast(message, { type: 'error' });
  }

  async function openEntri(domain: Domain): Promise<void> {
    if (isOpening) {
      return;
    }

    setIsOpening(true);
    try {
      // Mint on every open: the token expires after an hour and authorizes these exact records.
      const [sdk, token] = await Promise.all([loadEntriSdk(), domain.getEntriToken()]);
      sdk.connectDomain(toEntriConfig(token));
      onEntriClosed(function handleEntriClosed(detail): void {
        void handleClose(domain, detail);
      });
      posthog?.capture('entri_opened', { domainId: domain.id });
    } catch (error) {
      handleOpenError(error);
    } finally {
      setIsOpening(false);
    }
  }

  return { canConnect, isOpening, openEntri };
}

export default useEntriConnect;
